import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Upload, Search, RefreshCw, Trash2, FileSpreadsheet, MapPin, Briefcase, Calendar as CalendarIcon, Building2, Banknote, Users as UsersIcon, Globe, Layers, Package as PackageIcon, Weight, TrendingUp, ArrowUpDown, ArrowUp, ArrowDown, Check, ChevronDown, GitCompare, X, FileDown, FileText } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import SAR from '../components/SAR';
import { TabloEmployeeKpis } from '../components/TabloEmployeeKpis';
import {
  Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const KIND_META = {
  sharing: {
    title: 'Sharing',
    desc: 'تقرير مبيعات Sharing',
    // Filter keys → label mapping for the UI
    filters: [
      { key: 'المنطقة', label: 'المنطقة' },
      { key: 'المكتب', label: 'المكتب' },
      { key: 'Top Service Name', label: 'الخدمة' },
      { key: 'اليوم', label: 'اليوم' },
    ],
    // Table columns
    columns: [
      { key: 'date', label: 'التاريخ' },
      { key: 'اليوم', label: 'اليوم' },
      { key: 'المنطقة', label: 'المنطقة' },
      { key: 'المكتب', label: 'المكتب' },
      { key: 'Top Service Name', label: 'الخدمة' },
      { key: 'Income', label: 'الإيراد (ر.س)', numeric: true },
    ],
  },
  tablo: {
    title: 'Tableau',
    desc: 'تقرير Tableau',
    filters: [
      { key: 'Region Ar', label: 'المنطقة' },
      { key: 'City Ar', label: 'المدينة' },
      { key: 'Office Ar', label: 'المكتب' },
      { key: 'Top Service Type', label: 'الخدمة الرئيسية' },
      { key: 'Parent Service Type', label: 'الخدمة الأم' },
      { key: 'Service Type', label: 'نوع الخدمة' },
      { key: 'Country Ar', label: 'الدولة' },
      { key: 'User_Full_Name', label: 'الموظف' },
    ],
    columns: [
      { key: 'date', label: 'التاريخ' },
      { key: 'Item Code', label: 'Item Code' },
      { key: 'City Ar', label: 'المدينة' },
      { key: 'Office Ar', label: 'المكتب' },
      { key: 'Top Service Type', label: 'الخدمة الرئيسية' },
      { key: 'Service Type', label: 'نوع الخدمة' },
      { key: 'Country Ar', label: 'الدولة' },
      { key: 'User_Full_Name', label: 'الموظف' },
      { key: 'Total Items', label: 'العدد', numeric: true },
      { key: 'Total Weight', label: 'الوزن (كجم)', numeric: true },
      { key: 'Total Revenue', label: 'الإيراد (ر.س)', numeric: true },
    ],
  },
};

const PALETTE = ['#0F766E', '#1E3A8A', '#9333EA', '#DC2626', '#F59E0B', '#10B981', '#06B6D4', '#EC4899', '#84CC16', '#6366F1'];

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });

const scrollToId = (id) => {
  try {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  } catch (e) { /* noop */ }
};

export default function SuperTablo() {
  const { kind } = useParams();
  const meta = KIND_META[kind] || { title: kind, desc: '', filters: [], columns: [] };
  const [uploading, setUploading] = useState(false);
  const [uploadingTargets, setUploadingTargets] = useState(false);
  const [uploadingHistory, setUploadingHistory] = useState(false);
  // Compare dialog state
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareType, setCompareType] = useState('week');
  const [compareAYear, setCompareAYear] = useState(2026);
  const [compareAValue, setCompareAValue] = useState(25);
  const [compareBYear, setCompareBYear] = useState(2025);
  const [compareBValue, setCompareBValue] = useState(25);
  const [compareResult, setCompareResult] = useState(null);
  const [comparing, setComparing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [metaInfo, setMetaInfo] = useState(null);
  const [targetsMeta, setTargetsMeta] = useState(null);
  const [historyMeta, setHistoryMeta] = useState(null);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  // Filters
  const [search, setSearch] = useState('');
  const [filterValues, setFilterValues] = useState({}); // top-level: { 'Region Ar': 'المنطقة الشرقية', ... }
  const [colFilters, setColFilters] = useState({}); // inline column filters under the data table headers
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState('desc');
  // Period filter (week/month/quarter/year)
  const [periodType, setPeriodType] = useState(null); // 'week' | 'month' | 'quarter' | 'year' | null
  const [periodYear, setPeriodYear] = useState(null);
  const [periodValue, setPeriodValue] = useState(null);

  const fetchMeta = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/super/tablo/${kind}/meta`);
      setMetaInfo(r.data);
    } catch (err) { /* no data yet */ }
    if (kind === 'sharing') {
      try {
        const r2 = await axios.get(`${API_URL}/api/super/tablo/sharing/targets/meta`);
        setTargetsMeta(r2.data);
      } catch (err) { setTargetsMeta(null); }
      try {
        const r3 = await axios.get(`${API_URL}/api/super/tablo/sharing/history/meta`);
        setHistoryMeta(r3.data);
      } catch (err) { setHistoryMeta(null); }
    }
  }, [kind]);

  const filterParams = useMemo(() => {
    const p = { search: search || undefined };
    Object.entries(filterValues).forEach(([k, v]) => { if (v) p[k] = v; });
    // Column-level filters override top-level filters for the same key
    Object.entries(colFilters || {}).forEach(([k, v]) => { if (v) p[k] = v; });
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    if (periodType && periodYear) {
      p.period = periodType;
      p.period_year = periodYear;
      if (periodValue !== null && periodValue !== undefined) p.period_value = periodValue;
    }
    return p;
  }, [search, filterValues, colFilters, dateFrom, dateTo, periodType, periodYear, periodValue]);

  const fetchData = useCallback(async () => {
    const noIncome = !metaInfo || metaInfo.count === 0;
    const hasTargets = kind === 'sharing' && targetsMeta && targetsMeta.count > 0;
    const hasHistory = kind === 'sharing' && historyMeta && historyMeta.count > 0;
    const hasOptional = hasTargets || hasHistory;
    if (noIncome && !hasOptional) {
      setRows([]); setTotal(0); setSummary(null);
      return;
    }
    setLoading(true);
    try {
      if (noIncome && hasOptional) {
        const s = await axios.get(`${API_URL}/api/super/tablo/${kind}/summary`, { params: filterParams });
        setSummary(s.data); setRows([]); setTotal(0);
      } else {
        const [s, l] = await Promise.all([
          axios.get(`${API_URL}/api/super/tablo/${kind}/summary`, { params: filterParams }),
          axios.get(`${API_URL}/api/super/tablo/${kind}`, { params: { ...filterParams, page, page_size: pageSize, sort_by: sortBy || undefined, sort_dir: sortDir } }),
        ]);
        setSummary(s.data);
        setRows(l.data.rows || []);
        setTotal(l.data.total || 0);
      }
    } catch (err) {
      toast.error('فشل تحميل البيانات');
    } finally {
      setLoading(false);
    }
  }, [kind, metaInfo, targetsMeta, historyMeta, filterParams, page, pageSize, sortBy, sortDir]);

  useEffect(() => { fetchMeta(); }, [fetchMeta]);
  useEffect(() => { fetchData(); }, [fetchData]);
  // Reset filters when kind changes
  useEffect(() => { setFilterValues({}); setColFilters({}); setSearch(''); setDateFrom(''); setDateTo(''); setPage(1); setSortBy(null); setSortDir('desc'); setPeriodType(null); setPeriodYear(null); setPeriodValue(null); }, [kind]);

  const doExport = async (format) => {
    if (!hasData && !(kind === 'sharing' && (targetsMeta?.count > 0 || historyMeta?.count > 0))) {
      toast.error('لا توجد بيانات للتصدير');
      return;
    }
    try {
      // filterParams already reflects branch/region/office/search/date/period selections
      const params = new URLSearchParams();
      Object.entries(filterParams || {}).forEach(([k, v]) => {
        if (v === null || v === undefined || v === '') return;
        params.append(k, String(v));
      });
      const url = `${API_URL}/api/super/tablo/${kind}/export/${format}?${params.toString()}`;
      const r = await axios.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = blobUrl;
      const ts = new Date().toISOString().slice(0, 10);
      a.download = `tablo_${kind}_${ts}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(format === 'excel' ? 'تم تصدير Excel' : 'تم تصدير PDF');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'فشل التصدير');
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      toast.error('يجب أن يكون الملف بصيغة .xlsx');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API_URL}/api/super/tablo/${kind}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`تم استيراد ${r.data.imported_rows} صف بنجاح`);
      setPage(1);
      await fetchMeta();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل الاستيراد');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`حذف الملف السابق وجميع بيانات «${meta.title}»؟ لا يمكن التراجع.`)) return;
    try {
      await axios.delete(`${API_URL}/api/super/tablo/${kind}`);
      toast.success('تم حذف الملف السابق');
      setRows([]); setSummary(null); setTotal(0);
      await fetchMeta();
    } catch (err) {
      toast.error('فشل الحذف');
    }
  };

  const handleUploadTargets = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      toast.error('يجب أن يكون الملف بصيغة .xlsx');
      return;
    }
    setUploadingTargets(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API_URL}/api/super/tablo/sharing/targets/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`تم استيراد ${r.data.imported_rows} صف من ملف المستهدفات`);
      await fetchMeta();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل استيراد المستهدفات');
    } finally {
      setUploadingTargets(false);
      e.target.value = '';
    }
  };

  const handleDeleteTargets = async () => {
    if (!window.confirm('حذف ملف مستهدفات 2026؟ لا يمكن التراجع.')) return;
    try {
      await axios.delete(`${API_URL}/api/super/tablo/sharing/targets`);
      toast.success('تم حذف ملف المستهدفات');
      await fetchMeta();
    } catch (err) {
      toast.error('فشل الحذف');
    }
  };

  const handleUploadHistory = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      toast.error('يجب أن يكون الملف بصيغة .xlsx');
      return;
    }
    setUploadingHistory(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API_URL}/api/super/tablo/sharing/history/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`تم استيراد ${r.data.imported_rows} صف من محقق ${r.data.history_year || 'العام السابق'}`);
      await fetchMeta();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل استيراد محقق العام السابق');
    } finally {
      setUploadingHistory(false);
      e.target.value = '';
    }
  };

  const handleDeleteHistory = async () => {
    if (!window.confirm('حذف ملف محقق العام السابق؟ لا يمكن التراجع.')) return;
    try {
      await axios.delete(`${API_URL}/api/super/tablo/sharing/history`);
      toast.success('تم حذف ملف محقق العام السابق');
      await fetchMeta();
    } catch (err) {
      toast.error('فشل الحذف');
    }
  };

  const runCompare = async () => {
    setComparing(true);
    try {
      const params = {
        type: compareType,
        a_year: compareAYear,
        b_year: compareBYear,
      };
      if (compareType !== 'year') {
        params.a_value = compareAValue;
        params.b_value = compareBValue;
      }
      const r = await axios.get(`${API_URL}/api/super/tablo/sharing/compare`, { params });
      setCompareResult(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل تنفيذ المقارنة');
    } finally {
      setComparing(false);
    }
  };

  const resetFilters = () => {
    setSearch(''); setFilterValues({}); setColFilters({}); setDateFrom(''); setDateTo(''); setPage(1); setSortBy(null); setSortDir('desc');
    setPeriodType(null); setPeriodYear(null); setPeriodValue(null);
  };

  // Apply a drill-down filter from a chart slice click (toggles off if same value re-clicked)
  const applyDrillFilter = (key, value) => {
    if (!key || value === undefined || value === null || value === '') return;
    const current = colFilters[key] ?? filterValues[key];
    if (String(current) === String(value)) {
      // Toggle off — remove the filter
      setColFilters((s) => { const c = { ...s }; delete c[key]; return c; });
      setFilterValues((s) => { const c = { ...s }; delete c[key]; return c; });
      toast.success(`أُلغي فلتر «${value}»`);
    } else {
      setColFilters((s) => ({ ...s, [key]: String(value) }));
      toast.success(`تمت تصفية: ${value}`);
    }
    setPage(1);
  };
  const drillByDate = (dateStr) => {
    if (!dateStr) return;
    if (dateFrom === dateStr && dateTo === dateStr) {
      setDateFrom(''); setDateTo('');
      toast.success(`أُلغي فلتر التاريخ`);
    } else {
      setDateFrom(dateStr); setDateTo(dateStr);
      toast.success(`تمت تصفية: ${dateStr}`);
    }
    setPage(1);
  };
  // Active filter chips (for visible feedback + one-click clear)
  const activeFilters = Object.entries({ ...filterValues, ...colFilters }).filter(([_, v]) => v);
  const removeFilter = (key) => {
    setColFilters((s) => { const c = { ...s }; delete c[key]; return c; });
    setFilterValues((s) => { const c = { ...s }; delete c[key]; return c; });
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const hasData = (metaInfo?.count || 0) > 0;

  // Map each chart to its corresponding filter column (kind-aware)
  const chartKeys = kind === 'tablo' ? {
    region: 'Region Ar', office: 'Office Ar', service: 'Top Service Type',
    user: 'User_Full_Name', country: 'Country Ar', city: 'City Ar',
    service_type: 'Service Type',
  } : {
    region: 'المنطقة', office: 'المكتب', service: 'Top Service Name',
    day: 'اليوم', business_line: 'Business Line', type: 'Type',
  };
  const activeFor = (k) => colFilters[k] ?? filterValues[k];

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8" dir="rtl" data-testid={`super-tablo-${kind}`}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="space-y-2">
          <h1 className="text-2xl sm:text-3xl font-black text-primary flex items-center gap-2">
            <FileSpreadsheet className="h-7 w-7" />
            تقرير {meta.title}
          </h1>
          {/* Achievements file info */}
          {hasData ? (
            <div className="space-y-0.5">
              <p className="text-sm text-foreground/60">
                <span className="font-numbers font-bold">{fmt(metaInfo.count)}</span> صف
                {metaInfo.last_upload?.file_name && (
                  <> • <span className="font-mono text-xs text-foreground/50">{metaInfo.last_upload.file_name}</span></>
                )}
              </p>
              {metaInfo.last_upload?.uploaded_at && (
                <p className="text-xs text-foreground/55 flex flex-wrap items-center gap-x-2 gap-y-0.5" data-testid={`tablo-${kind}-last-upload`}>
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-bold border border-emerald-200">
                    آخر تحديث المحقق
                  </span>
                  <span className="font-numbers" dir="ltr">
                    {new Date(metaInfo.last_upload.uploaded_at).toLocaleString('ar-EG', { dateStyle: 'medium', timeStyle: 'short' })}
                  </span>
                  {metaInfo.last_upload?.uploaded_by_name && (
                    <span className="text-foreground/45">• بواسطة {metaInfo.last_upload.uploaded_by_name}</span>
                  )}
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-foreground/60">لم يتم رفع ملف المحقق بعد</p>
          )}
          {/* Targets file info — Sharing only */}
          {kind === 'sharing' && (
            targetsMeta && targetsMeta.count > 0 ? (
              <div className="space-y-0.5">
                <p className="text-sm text-foreground/60">
                  <span className="font-numbers font-bold">{fmt(targetsMeta.count)}</span> صف مستهدفات
                  {targetsMeta.last_upload?.file_name && (
                    <> • <span className="font-mono text-xs text-foreground/50">{targetsMeta.last_upload.file_name}</span></>
                  )}
                </p>
                {targetsMeta.last_upload?.uploaded_at && (
                  <p className="text-xs text-foreground/55 flex flex-wrap items-center gap-x-2 gap-y-0.5" data-testid="tablo-sharing-targets-last-upload">
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 font-bold border border-amber-200">
                      آخر تحديث المستهدف
                    </span>
                    <span className="font-numbers" dir="ltr">
                      {new Date(targetsMeta.last_upload.uploaded_at).toLocaleString('ar-EG', { dateStyle: 'medium', timeStyle: 'short' })}
                    </span>
                    {targetsMeta.last_upload?.uploaded_by_name && (
                      <span className="text-foreground/45">• بواسطة {targetsMeta.last_upload.uploaded_by_name}</span>
                    )}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-foreground/60">لم يتم رفع ملف المستهدفات بعد</p>
            )
          )}
          {/* History (previous year) file info — Sharing only */}
          {kind === 'sharing' && (
            historyMeta && historyMeta.count > 0 ? (
              <div className="space-y-0.5">
                <p className="text-sm text-foreground/60">
                  <span className="font-numbers font-bold">{fmt(historyMeta.count)}</span> صف محقق {historyMeta.last_upload?.history_year || ''}
                  {historyMeta.last_upload?.file_name && (
                    <> • <span className="font-mono text-xs text-foreground/50">{historyMeta.last_upload.file_name}</span></>
                  )}
                </p>
                {historyMeta.last_upload?.uploaded_at && (
                  <p className="text-xs text-foreground/55 flex flex-wrap items-center gap-x-2 gap-y-0.5" data-testid="tablo-sharing-history-last-upload">
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 font-bold border border-blue-200">
                      آخر تحديث محقق {historyMeta.last_upload?.history_year || 'سابق'}
                    </span>
                    <span className="font-numbers" dir="ltr">
                      {new Date(historyMeta.last_upload.uploaded_at).toLocaleString('ar-EG', { dateStyle: 'medium', timeStyle: 'short' })}
                    </span>
                    {historyMeta.last_upload?.uploaded_by_name && (
                      <span className="text-foreground/45">• بواسطة {historyMeta.last_upload.uploaded_by_name}</span>
                    )}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-foreground/60">لم يتم رفع ملف محقق العام السابق بعد</p>
            )
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Achievements upload (renamed: محقق 2026) */}
          <label className="inline-flex">
            <input
              type="file"
              accept=".xlsx"
              onChange={handleUpload}
              className="hidden"
              data-testid={`tablo-${kind}-upload-input`}
            />
            <Button asChild disabled={uploading} data-testid={`tablo-${kind}-upload-button`}>
              <span className="cursor-pointer flex items-center gap-2">
                <Upload className="h-4 w-4" />
                {uploading ? 'جاري الرفع...' : (kind === 'sharing' ? 'محقق 2026' : (hasData ? 'تحديث الملف' : 'رفع الملف'))}
              </span>
            </Button>
          </label>
          {hasData && (
            <Button variant="outline" onClick={handleDelete} className="text-destructive border-destructive/40" data-testid={`tablo-${kind}-delete-button`}>
              <Trash2 className="h-4 w-4 ml-1" />
              {kind === 'sharing' ? 'حذف ملف المحقق' : 'حذف الملف السابق'}
            </Button>
          )}
          {/* Export buttons — always visible when there's data (respect current filters). */}
          {(hasData || (kind === 'sharing' && (targetsMeta?.count > 0 || historyMeta?.count > 0))) && (
            <>
              <Button
                variant="outline"
                onClick={() => doExport('excel')}
                className="text-emerald-700 border-emerald-500/50 hover:bg-emerald-50"
                data-testid={`tablo-${kind}-export-excel`}
              >
                <FileDown className="h-4 w-4 ml-1" />
                تصدير Excel
              </Button>
              <Button
                variant="outline"
                onClick={() => doExport('pdf')}
                className="text-rose-700 border-rose-500/50 hover:bg-rose-50"
                data-testid={`tablo-${kind}-export-pdf`}
              >
                <FileText className="h-4 w-4 ml-1" />
                تصدير PDF
              </Button>
            </>
          )}
          {/* Targets upload — Sharing only */}
          {kind === 'sharing' && (
            <>
              <label className="inline-flex">
                <input
                  type="file"
                  accept=".xlsx"
                  onChange={handleUploadTargets}
                  className="hidden"
                  data-testid="tablo-sharing-targets-upload-input"
                />
                <Button
                  asChild
                  disabled={uploadingTargets}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="tablo-sharing-targets-upload-button"
                >
                  <span className="cursor-pointer flex items-center gap-2">
                    <Upload className="h-4 w-4" />
                    {uploadingTargets ? 'جاري الرفع...' : 'مستهدفات 2026'}
                  </span>
                </Button>
              </label>
              {targetsMeta && targetsMeta.count > 0 && (
                <Button
                  variant="outline"
                  onClick={handleDeleteTargets}
                  className="text-destructive border-destructive/40"
                  data-testid="tablo-sharing-targets-delete-button"
                >
                  <Trash2 className="h-4 w-4 ml-1" />
                  حذف المستهدفات
                </Button>
              )}
              {/* History (previous year) upload */}
              <label className="inline-flex">
                <input
                  type="file"
                  accept=".xlsx"
                  onChange={handleUploadHistory}
                  className="hidden"
                  data-testid="tablo-sharing-history-upload-input"
                />
                <Button
                  asChild
                  disabled={uploadingHistory}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="tablo-sharing-history-upload-button"
                >
                  <span className="cursor-pointer flex items-center gap-2">
                    <Upload className="h-4 w-4" />
                    {uploadingHistory ? 'جاري الرفع...' : 'محقق 2025'}
                  </span>
                </Button>
              </label>
              {historyMeta && historyMeta.count > 0 && (
                <Button
                  variant="outline"
                  onClick={handleDeleteHistory}
                  className="text-destructive border-destructive/40"
                  data-testid="tablo-sharing-history-delete-button"
                >
                  <Trash2 className="h-4 w-4 ml-1" />
                  حذف محقق {historyMeta.last_upload?.history_year || '2025'}
                </Button>
              )}
              {/* Comparison dialog trigger */}
              <Button
                variant="outline"
                onClick={() => setCompareOpen(true)}
                className="bg-purple-600 hover:bg-purple-700 text-white border-purple-700 hover:text-white"
                data-testid="tablo-sharing-compare-button"
              >
                <GitCompare className="h-4 w-4 ml-1" />
                مقارنة
              </Button>
            </>
          )}
        </div>
      </div>

      {!hasData && !(kind === 'sharing' && (targetsMeta?.count > 0 || historyMeta?.count > 0)) ? (
        <Card className="p-12 text-center">
          <FileSpreadsheet className="h-16 w-16 text-foreground/30 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-foreground/70">لا توجد بيانات</h2>
          <p className="text-sm text-foreground/50 mt-2">
            ارفع ملف Excel بصيغة .xlsx ليتم استيراده وعرضه هنا.
          </p>
        </Card>
      ) : (
        <>
          {/* Period selector — week / month / quarter / year / custom */}
          {(metaInfo?.years || []).length > 0 && (
            <PeriodSelector
              years={metaInfo.years}
              weeksByYear={metaInfo.weeks_by_year || {}}
              periodType={periodType}
              periodYear={periodYear}
              periodValue={periodValue}
              onChange={(t, y, v) => { setPeriodType(t); setPeriodYear(y); setPeriodValue(v); setPage(1); }}
              dateFrom={dateFrom}
              dateTo={dateTo}
              setDateFrom={(v) => { setDateFrom(v); setPage(1); }}
              setDateTo={(v) => { setDateTo(v); setPage(1); }}
            />
          )}

          {/* Active drill-down filters bar */}
          {(activeFilters.length > 0 || (dateFrom && dateTo && dateFrom === dateTo) || (periodType && periodType !== 'custom' && periodYear) || (periodType === 'custom' && (dateFrom || dateTo))) && (
            <Card className="p-3 bg-primary/5 border-primary/30">
              <div className="flex items-center flex-wrap gap-2">
                <span className="text-xs font-bold text-primary">الفلاتر النشطة:</span>
                {periodType && periodType !== 'custom' && periodYear && (
                  <button
                    type="button"
                    onClick={() => { setPeriodType(null); setPeriodYear(null); setPeriodValue(null); setPage(1); }}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-600 text-white text-[11px] font-bold hover:bg-emerald-700 transition-colors"
                    data-testid="active-period-chip"
                  >
                    <span className="opacity-80">الفترة:</span>
                    <span>{periodLabel(periodType, periodYear, periodValue)}</span>
                    <span className="opacity-80 text-sm leading-none">×</span>
                  </button>
                )}
                {periodType === 'custom' && (dateFrom || dateTo) && (
                  <button
                    type="button"
                    onClick={() => { setPeriodType(null); setPeriodYear(null); setPeriodValue(null); setDateFrom(''); setDateTo(''); setPage(1); }}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-600 text-white text-[11px] font-bold hover:bg-emerald-700 transition-colors"
                    data-testid="active-period-chip"
                  >
                    <span className="opacity-80">نطاق:</span>
                    <span>{(dateFrom || '…')} → {(dateTo || '…')}</span>
                    <span className="opacity-80 text-sm leading-none">×</span>
                  </button>
                )}
                {activeFilters.map(([k, v]) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => removeFilter(k)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary text-white text-[11px] font-bold hover:bg-primary/80 transition-colors"
                    data-testid={`active-filter-chip-${k}`}
                  >
                    <span className="opacity-80">{(meta.filters.find((f) => f.key === k) || meta.columns.find((c) => c.key === k))?.label || k}:</span>
                    <span>{String(v).split(',').join('، ')}</span>
                    <span className="opacity-80 text-sm leading-none">×</span>
                  </button>
                ))}
                {dateFrom && dateTo && dateFrom === dateTo && (
                  <button
                    type="button"
                    onClick={() => { setDateFrom(''); setDateTo(''); setPage(1); }}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary text-white text-[11px] font-bold hover:bg-primary/80 transition-colors"
                  >
                    <span className="opacity-80">التاريخ:</span>
                    <span>{dateFrom}</span>
                    <span className="opacity-80 text-sm leading-none">×</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => doExport('excel')}
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-600 text-white text-[11px] font-bold hover:bg-emerald-700 transition-colors mr-auto shadow-sm"
                  data-testid={`filtered-export-excel-${kind}`}
                  title="تصدير النتائج المُصفّاة كملف Excel"
                >
                  <FileDown className="h-3.5 w-3.5" />
                  تصدير Excel للنتيجة المُصفّاة
                </button>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="text-[11px] font-bold text-destructive hover:underline"
                  data-testid="clear-all-filters"
                >
                  مسح الكل
                </button>
              </div>
            </Card>
          )}

          {/* KPI Cards — circular interactive */}
          {summary && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              <KpiCircle icon={<Banknote />} label="إجمالي المحقق" value={fmt(summary.total_income)} suffix={<SAR />} accent="emerald" onClick={() => scrollToId('chart-by-region')} testid="kpi-total-income" />
              {kind === 'sharing' && summary.total_target > 0 && (
                <>
                  <KpiCircle icon={<TrendingUp />} label="إجمالي المستهدف" value={fmt(summary.total_target)} suffix={<SAR />} accent="amber" testid="kpi-total-target" />
                  <KpiCircle
                    icon={<Banknote />}
                    label="نسبة الإنجاز"
                    value={`${((summary.total_income / summary.total_target) * 100).toFixed(1)}%`}
                    sub={<span className="font-numbers">{fmt(summary.total_target - summary.total_income)} متبقي</span>}
                    accent={(summary.total_income / summary.total_target) >= 1 ? 'emerald' : (summary.total_income / summary.total_target) >= 0.7 ? 'blue' : 'rose'}
                    testid="kpi-achievement-pct"
                  />
                </>
              )}
              {kind === 'sharing' && summary.prev_year_total > 0 && (
                <KpiCircle
                  icon={<Banknote />}
                  label={`محقق ${summary.prev_year_label || 'العام السابق'}`}
                  value={fmt(summary.prev_year_total)}
                  suffix={<SAR />}
                  accent="blue"
                  testid="kpi-prev-year-total"
                />
              )}
              <KpiCircle icon={<Briefcase />} label="عدد المعاملات" value={fmt(summary.total_rows)} accent="blue" onClick={() => scrollToId('data-table')} testid="kpi-total-rows" />
              <KpiCircle icon={<Building2 />} label="عدد المكاتب" value={fmt(summary.n_offices || 0)} accent="indigo" onClick={() => scrollToId('chart-by-office')} testid="kpi-n-offices" />
              <KpiCircle icon={<MapPin />} label="عدد المناطق" value={fmt(summary.n_regions || 0)} accent="cyan" onClick={() => scrollToId('chart-by-region')} testid="kpi-n-regions" />
              {kind === 'tablo' && (
                <>
                  <KpiCircle icon={<UsersIcon />} label="عدد الموظفين" value={fmt(summary.n_users || 0)} accent="purple" onClick={() => scrollToId('chart-by-user')} testid="kpi-n-users" />
                  <KpiCircle icon={<Globe />} label="عدد الدول" value={fmt(summary.n_countries || 0)} accent="rose" onClick={() => scrollToId('chart-by-country')} testid="kpi-n-countries" />
                  <KpiCircle icon={<Building2 />} label="عدد المدن" value={fmt(summary.n_cities || 0)} accent="amber" onClick={() => scrollToId('chart-by-city')} testid="kpi-n-cities" />
                  <KpiCircle icon={<PackageIcon />} label="إجمالي العدد" value={fmt(summary.total_items || 0)} accent="amber" testid="kpi-total-items" />
                  <KpiCircle icon={<Weight />} label="إجمالي الوزن" value={fmt(summary.total_weight || 0)} suffix={<span className="text-[10px]">كجم</span>} accent="cyan" testid="kpi-total-weight" />
                </>
              )}
              <KpiCircle icon={<Layers />} label="عدد الخدمات" value={fmt(summary.n_services || 0)} accent="indigo" onClick={() => scrollToId('chart-by-service')} testid="kpi-n-services" />
              <KpiCircle icon={<TrendingUp />} label="أعلى يوم" value={summary.peak_date || '—'} sub={<>{fmt(summary.peak_date_income || 0)} <SAR /></>} accent="rose" onClick={() => scrollToId('chart-by-date')} testid="kpi-peak-date" small />
              <KpiCircle icon={<Banknote />} label="متوسط/مكتب" value={fmt(summary.avg_per_office || 0)} suffix={<SAR />} accent="emerald" small testid="kpi-avg-office" />
              <KpiCircle icon={<Banknote />} label="متوسط/يوم" value={fmt(summary.avg_per_day || 0)} suffix={<SAR />} accent="blue" small testid="kpi-avg-day" />
              <KpiCircle icon={<CalendarIcon />} label="الفترة" value={`${summary.min_date || '—'}`} sub={`→ ${summary.max_date || '—'}`} accent="slate" small testid="kpi-period" />
            </div>
          )}

          {/* Filters */}
          <Card className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">بحث</Label>
                <div className="relative">
                  <Search className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/40" />
                  <Input
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    placeholder="مكتب، خدمة، منطقة..."
                    className="pr-8 h-9 text-sm"
                    data-testid={`tablo-${kind}-search`}
                  />
                </div>
              </div>
              {meta.filters.map((f) => {
                const v = colFilters[f.key] ?? filterValues[f.key] ?? '';
                const sel = v ? String(v).split(',').map((s) => s.trim()).filter(Boolean) : [];
                return (
                  <div key={f.key}>
                    <Label className="text-xs">{f.label}</Label>
                    <MultiSelectFilter
                      big
                      options={metaInfo?.filters?.[f.key] || []}
                      selected={sel}
                      onChange={(arr) => {
                        setColFilters((s) => { const c = { ...s }; delete c[f.key]; return c; });
                        setFilterValues((s) => ({ ...s, [f.key]: arr.join(',') }));
                        setPage(1);
                      }}
                      testid={`tablo-${kind}-filter-${f.key}`}
                    />
                  </div>
                );
              })}
              <div>
                <Label className="text-xs">من تاريخ</Label>
                <Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} className="h-9 text-sm" />
              </div>
              <div>
                <Label className="text-xs">إلى تاريخ</Label>
                <Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} className="h-9 text-sm" />
              </div>
              <div className="flex items-end">
                <Button variant="outline" onClick={resetFilters} className="w-full h-9 gap-2 text-sm" data-testid={`tablo-${kind}-reset`}>
                  <RefreshCw className="h-4 w-4" />
                  إعادة تعيين
                </Button>
              </div>
            </div>
          </Card>

          {/* Charts — all circular (donut, click-to-filter) */}
          {summary && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ChartCard id="chart-by-region" title="الإيرادات حسب المنطقة">
                <DonutChart data={summary.by_region || summary.by_area || []} total={summary.total_income}
                  onSliceClick={(n) => applyDrillFilter(chartKeys.region, n)} activeName={activeFor(chartKeys.region)} />
              </ChartCard>
              <ChartCard id="chart-by-service" title="الإيرادات حسب الخدمة">
                <DonutChart data={summary.by_service || []} total={summary.total_income}
                  onSliceClick={(n) => applyDrillFilter(chartKeys.service, n)} activeName={activeFor(chartKeys.service)} />
              </ChartCard>
              <ChartCard id="chart-by-office" title="أفضل 20 مكتب">
                <DonutChart data={summary.by_office || []} total={summary.total_income} large
                  onSliceClick={(n) => applyDrillFilter(chartKeys.office, n)} activeName={activeFor(chartKeys.office)} />
              </ChartCard>
              <ChartCard id="chart-by-date" title="الإيرادات اليومية (أعلى الأيام)">
                <DonutChart data={(summary.by_date || []).slice().sort((a,b)=>(b.income||0)-(a.income||0)).slice(0,15)} total={summary.total_income}
                  onSliceClick={drillByDate} activeName={dateFrom && dateFrom === dateTo ? dateFrom : null} />
              </ChartCard>
            </div>
          )}

          {/* Extra charts — kind-specific */}
          {summary && kind === 'tablo' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {(summary.by_user || []).length > 0 && (
                <ChartCard id="chart-by-user" title="أفضل 15 موظف (الإيراد)">
                  <DonutChart data={summary.by_user} total={summary.total_income} large
                    onSliceClick={(n) => applyDrillFilter(chartKeys.user, n)} activeName={activeFor(chartKeys.user)} />
                </ChartCard>
              )}
              {(summary.by_country || []).length > 0 && (
                <ChartCard id="chart-by-country" title="أعلى 15 دولة">
                  <DonutChart data={summary.by_country} total={summary.total_income} large
                    onSliceClick={(n) => applyDrillFilter(chartKeys.country, n)} activeName={activeFor(chartKeys.country)} />
                </ChartCard>
              )}
              {(summary.by_city || []).length > 0 && (
                <ChartCard id="chart-by-city" title="أعلى 15 مدينة">
                  <DonutChart data={summary.by_city} total={summary.total_income} large
                    onSliceClick={(n) => applyDrillFilter(chartKeys.city, n)} activeName={activeFor(chartKeys.city)} />
                </ChartCard>
              )}
              {(summary.by_service_type || []).length > 0 && (
                <ChartCard title="توزيع نوع الخدمة">
                  <DonutChart data={summary.by_service_type} total={summary.total_income} large
                    onSliceClick={(n) => applyDrillFilter(chartKeys.service_type, n)} activeName={activeFor(chartKeys.service_type)} />
                </ChartCard>
              )}
            </div>
          )}
          {summary && kind === 'sharing' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {(summary.by_business_line || []).length > 0 && (
                <ChartCard title="حسب خط الأعمال (Business Line)">
                  <DonutChart data={summary.by_business_line} total={summary.total_income}
                    onSliceClick={(n) => applyDrillFilter(chartKeys.business_line, n)} activeName={activeFor(chartKeys.business_line)} />
                </ChartCard>
              )}
              {(summary.by_type || []).length > 0 && (
                <ChartCard title="حسب النوع (Type)">
                  <DonutChart data={summary.by_type} total={summary.total_income}
                    onSliceClick={(n) => applyDrillFilter(chartKeys.type, n)} activeName={activeFor(chartKeys.type)} />
                </ChartCard>
              )}
              {(summary.by_day || []).length > 0 && (
                <ChartCard title="حسب اليوم">
                  <DonutChart data={summary.by_day} total={summary.total_income}
                    onSliceClick={(n) => applyDrillFilter(chartKeys.day, n)} activeName={activeFor(chartKeys.day)} />
                </ChartCard>
              )}
            </div>
          )}

          {kind === 'tablo' && hasData && (
            <TabloEmployeeKpis filterParams={filterParams} onExport={() => doExport('excel')} />
          )}

          {/* Data Table */}
          <Card id="data-table" className="overflow-hidden scroll-mt-24">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between bg-accent/20 gap-3 flex-wrap">
              <h3 className="font-bold text-sm">جدول البيانات ({fmt(total)} صف{total > pageSize ? `، صفحة ${page}/${totalPages}` : ''})</h3>
              <div className="flex items-center gap-2">
                {(Object.values(colFilters || {}).some((v) => v) || sortBy) && (
                  <Button variant="outline" size="sm" onClick={() => { setColFilters({}); setSortBy(null); setPage(1); }} className="h-7 text-xs gap-1" data-testid={`tablo-${kind}-clear-col-filters`}>
                    <RefreshCw className="h-3 w-3" />
                    مسح فلاتر الأعمدة
                  </Button>
                )}
                {loading && <span className="text-xs text-foreground/50">جاري التحميل...</span>}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    {meta.columns.map((c) => {
                      const isSorted = sortBy === c.key;
                      const SortIcon = !isSorted ? ArrowUpDown : (sortDir === 'asc' ? ArrowUp : ArrowDown);
                      return (
                        <Th key={c.key} className={c.numeric ? 'text-left' : ''}>
                          <button
                            type="button"
                            onClick={() => {
                              if (sortBy === c.key) {
                                setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                              } else {
                                setSortBy(c.key); setSortDir('desc');
                              }
                              setPage(1);
                            }}
                            className={`inline-flex items-center gap-1 hover:text-primary transition-colors ${c.numeric ? 'flex-row-reverse' : ''} ${isSorted ? 'text-primary font-extrabold' : ''}`}
                            data-testid={`tablo-${kind}-sort-${c.key}`}
                          >
                            <span>{c.label}</span>
                            <SortIcon className="h-3 w-3 opacity-60" />
                          </button>
                        </Th>
                      );
                    })}
                  </tr>
                  {/* Inline column filters */}
                  <tr className="bg-accent/40 border-t border-border/60">
                    {meta.columns.map((c) => {
                      const isDate = c.key === 'date';
                      const isNumeric = c.numeric;
                      // Use Select if backend provided distinct options for this column
                      const opts = metaInfo?.filters?.[c.key];
                      const val = colFilters?.[c.key] || '';
                      const setVal = (v) => {
                        setColFilters((s) => ({ ...s, [c.key]: v }));
                        setPage(1);
                      };
                      if (isDate) {
                        return (
                          <td key={c.key} className="px-2 py-1.5">
                            <div className="flex gap-1">
                              <input
                                type="date"
                                value={dateFrom}
                                onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                                className="w-full h-7 text-[10px] px-1 rounded border border-border bg-background"
                                title="من تاريخ"
                                data-testid={`tablo-${kind}-col-date-from`}
                              />
                              <input
                                type="date"
                                value={dateTo}
                                onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                                className="w-full h-7 text-[10px] px-1 rounded border border-border bg-background"
                                title="إلى تاريخ"
                                data-testid={`tablo-${kind}-col-date-to`}
                              />
                            </div>
                          </td>
                        );
                      }
                      if (opts && opts.length > 0 && opts.length <= 1000) {
                        // val is a comma-separated string. Convert to array for multi-select.
                        const selectedArr = (val ? String(val).split(',').map((s) => s.trim()).filter(Boolean) : []);
                        const onSelectedChange = (arr) => setVal(arr.join(','));
                        return (
                          <td key={c.key} className="px-2 py-1.5">
                            <MultiSelectFilter
                              options={opts}
                              selected={selectedArr}
                              onChange={onSelectedChange}
                              testid={`tablo-${kind}-col-filter-${c.key}`}
                            />
                          </td>
                        );
                      }
                      return (
                        <td key={c.key} className="px-2 py-1.5">
                          <input
                            type={isNumeric ? 'number' : 'text'}
                            value={val}
                            onChange={(e) => setVal(e.target.value)}
                            placeholder="تصفية..."
                            className={`w-full h-7 text-[11px] px-2 rounded border border-border bg-background placeholder:text-foreground/30 ${isNumeric ? 'text-left' : ''}`}
                            data-testid={`tablo-${kind}-col-filter-${c.key}`}
                          />
                        </td>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td colSpan={meta.columns.length} className="py-8 text-center text-foreground/50">لا توجد نتائج</td></tr>
                  ) : rows.map((r, i) => (
                    <tr key={`${r.date}-${i}`} className="border-b border-border hover:bg-accent/20">
                      {meta.columns.map((c) => (
                        <Td key={c.key} className={c.numeric ? 'text-left font-numbers font-bold' : ''}>
                          {c.numeric ? fmt(r[c.key]) : (r[c.key] || '—')}
                        </Td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="px-4 py-3 border-t border-border flex items-center justify-center gap-2 bg-accent/20">
                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>السابق</Button>
                <span className="text-sm font-bold px-3">{page} / {totalPages}</span>
                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>التالي</Button>
              </div>
            )}
          </Card>
        </>
      )}
      {/* ─── Comparison Dialog ─────────────────────────────────────────── */}
      <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitCompare className="h-5 w-5 text-purple-600" />
              مقارنة الفترات
            </DialogTitle>
          </DialogHeader>

          {/* Period type selector */}
          <div className="flex flex-wrap gap-2">
            {[
              { v: 'week', l: 'أسبوع' },
              { v: 'month', l: 'شهر' },
              { v: 'quarter', l: 'ربع سنة' },
              { v: 'year', l: 'سنة' },
            ].map((o) => (
              <Button
                key={o.v}
                size="sm"
                variant={compareType === o.v ? 'default' : 'outline'}
                onClick={() => setCompareType(o.v)}
                data-testid={`compare-type-${o.v}`}
              >
                {o.l}
              </Button>
            ))}
          </div>

          {/* Two period pickers side by side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Period A */}
            <Card className="p-4 border-emerald-200 bg-emerald-50/40">
              <p className="text-sm font-bold text-emerald-800 mb-2">الفترة الأولى (A)</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">السنة</Label>
                  <Select value={String(compareAYear)} onValueChange={(v) => setCompareAYear(Number(v))}>
                    <SelectTrigger data-testid="compare-a-year"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {compareType !== 'year' && (
                  <div>
                    <Label className="text-xs">
                      {compareType === 'week' ? 'الأسبوع' : compareType === 'month' ? 'الشهر' : 'الربع'}
                    </Label>
                    <Select value={String(compareAValue)} onValueChange={(v) => setCompareAValue(Number(v))}>
                      <SelectTrigger data-testid="compare-a-value"><SelectValue /></SelectTrigger>
                      <SelectContent className="max-h-64">
                        {(compareType === 'week' ? Array.from({ length: 53 }, (_, i) => i + 1)
                          : compareType === 'month' ? Array.from({ length: 12 }, (_, i) => i + 1)
                          : [1, 2, 3, 4]
                        ).map(v => (
                          <SelectItem key={v} value={String(v)}>
                            {compareType === 'month' ? MONTH_AR[v] : v}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </Card>
            {/* Period B */}
            <Card className="p-4 border-blue-200 bg-blue-50/40">
              <p className="text-sm font-bold text-blue-800 mb-2">الفترة الثانية (B)</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">السنة</Label>
                  <Select value={String(compareBYear)} onValueChange={(v) => setCompareBYear(Number(v))}>
                    <SelectTrigger data-testid="compare-b-year"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {compareType !== 'year' && (
                  <div>
                    <Label className="text-xs">
                      {compareType === 'week' ? 'الأسبوع' : compareType === 'month' ? 'الشهر' : 'الربع'}
                    </Label>
                    <Select value={String(compareBValue)} onValueChange={(v) => setCompareBValue(Number(v))}>
                      <SelectTrigger data-testid="compare-b-value"><SelectValue /></SelectTrigger>
                      <SelectContent className="max-h-64">
                        {(compareType === 'week' ? Array.from({ length: 53 }, (_, i) => i + 1)
                          : compareType === 'month' ? Array.from({ length: 12 }, (_, i) => i + 1)
                          : [1, 2, 3, 4]
                        ).map(v => (
                          <SelectItem key={v} value={String(v)}>
                            {compareType === 'month' ? MONTH_AR[v] : v}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </Card>
          </div>

          <Button
            onClick={runCompare}
            disabled={comparing}
            className="w-full bg-purple-600 hover:bg-purple-700"
            data-testid="compare-run"
          >
            <GitCompare className="h-4 w-4 ml-2" />
            {comparing ? 'جاري المقارنة...' : 'تنفيذ المقارنة'}
          </Button>

          {/* Results */}
          {compareResult && (
            <div className="space-y-4 mt-2 pt-4 border-t" data-testid="compare-result">
              {/* Totals */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Card className="p-3 border-emerald-200 bg-emerald-50">
                  <p className="text-xs text-emerald-700 font-bold">{compareResult.a.label}</p>
                  <p className="text-2xl font-black font-numbers text-emerald-700">
                    {fmt(compareResult.a.total_income)} <SAR size="0.85em" />
                  </p>
                  <p className="text-[11px] text-emerald-600">{fmt(compareResult.a.total_rows)} عملية</p>
                </Card>
                <Card className="p-3 border-blue-200 bg-blue-50">
                  <p className="text-xs text-blue-700 font-bold">{compareResult.b.label}</p>
                  <p className="text-2xl font-black font-numbers text-blue-700">
                    {fmt(compareResult.b.total_income)} <SAR size="0.85em" />
                  </p>
                  <p className="text-[11px] text-blue-600">{fmt(compareResult.b.total_rows)} عملية</p>
                </Card>
                <Card className={`p-3 ${compareResult.delta_total >= 0 ? 'border-emerald-300 bg-emerald-50/60' : 'border-rose-300 bg-rose-50/60'}`}>
                  <p className="text-xs font-bold text-foreground/70">الفرق (A − B)</p>
                  <p className={`text-2xl font-black font-numbers ${compareResult.delta_total >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                    {compareResult.delta_total >= 0 ? '+' : ''}{fmt(compareResult.delta_total)} <SAR size="0.85em" />
                  </p>
                  <p className={`text-[11px] font-bold ${compareResult.delta_total >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {compareResult.delta_pct >= 0 ? '+' : ''}{compareResult.delta_pct.toFixed(1)}%
                  </p>
                </Card>
              </div>

              {/* Dimension tables */}
              {[
                { key: 'by_region', title: 'حسب المنطقة' },
                { key: 'by_office', title: 'حسب المكتب (أعلى 15)' },
                { key: 'by_service', title: 'حسب الخدمة' },
              ].map((dim) => {
                const rows = (compareResult[dim.key] || []).slice(0, dim.key === 'by_office' ? 15 : 30);
                if (!rows.length) return null;
                return (
                  <Card key={dim.key} className="p-3" data-testid={`compare-${dim.key}`}>
                    <p className="text-sm font-bold mb-2">{dim.title}</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b text-foreground/60">
                            <th className="text-right py-1.5 px-2 font-bold">الاسم</th>
                            <th className="text-left py-1.5 px-2 font-bold text-emerald-700">A</th>
                            <th className="text-left py-1.5 px-2 font-bold text-blue-700">B</th>
                            <th className="text-left py-1.5 px-2 font-bold">الفرق</th>
                            <th className="text-left py-1.5 px-2 font-bold">%</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((r) => (
                            <tr key={r.name} className="border-b border-border/40 hover:bg-accent/30">
                              <td className="py-1.5 px-2 font-medium">{r.name || '—'}</td>
                              <td className="py-1.5 px-2 font-numbers text-left text-emerald-700">{fmt(r.a)}</td>
                              <td className="py-1.5 px-2 font-numbers text-left text-blue-700">{fmt(r.b)}</td>
                              <td className={`py-1.5 px-2 font-numbers text-left font-bold ${r.delta >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>
                                {r.delta >= 0 ? '+' : ''}{fmt(r.delta)}
                              </td>
                              <td className={`py-1.5 px-2 font-numbers text-left font-bold ${r.pct >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>
                                {r.pct >= 0 ? '+' : ''}{r.pct.toFixed(1)}%
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setCompareOpen(false); setCompareResult(null); }} data-testid="compare-close">
              <X className="h-4 w-4 ml-1" />
              إغلاق
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const MONTH_AR = ['', 'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];

function periodLabel(type, year, value) {
  if (!type || (!year && type !== 'custom')) return '';
  if (type === 'year') return `سنة ${year}`;
  if (type === 'month') return `${MONTH_AR[value] || value} ${year}`;
  if (type === 'quarter') return `الربع ${value} - ${year}`;
  if (type === 'week') return `أسبوع ${value} - ${year}`;
  if (type === 'custom') return `نطاق مخصص`;
  return '';
}

function PeriodSelector({ years, weeksByYear, periodType, periodYear, periodValue, onChange, dateFrom, dateTo, setDateFrom, setDateTo }) {
  const defaultYear = (years && years[years.length - 1]) || new Date().getFullYear();
  const y = periodYear || defaultYear;
  const PERIODS = [
    { key: 'week',    label: 'أسبوع' },
    { key: 'month',   label: 'شهر' },
    { key: 'quarter', label: 'ربع سنوي' },
    { key: 'year',    label: 'سنوي' },
    { key: 'custom',  label: 'نطاق محدد' },
  ];
  const handlePickType = (t) => {
    if (periodType === t) {
      // Toggle off
      onChange(null, null, null);
      if (t === 'custom') { setDateFrom(''); setDateTo(''); }
      return;
    }
    // Pick a sensible default value
    let v = null;
    if (t === 'week') {
      const ws = weeksByYear[String(y)] || [];
      v = ws.length ? ws[ws.length - 1] : null;
    } else if (t === 'month') {
      v = new Date().getMonth() + 1;
    } else if (t === 'quarter') {
      v = Math.floor(new Date().getMonth() / 3) + 1;
    } else if (t === 'year' || t === 'custom') {
      v = null;
    }
    onChange(t, t === 'custom' ? null : y, v);
  };
  // Show every week of the selected year (1..52 + week 53 if applicable) so the
  // manager can pick future weeks and see target numbers — not just weeks that
  // already have achieved data. We compute the Sun-based week count for `y`.
  const sundayWeekCount = (yr) => {
    if (!yr) return 52;
    // Sunday week 1 = the week containing Jan 1. Week N starts on Sunday.
    const jan1 = new Date(Date.UTC(yr, 0, 1));
    const dec31 = new Date(Date.UTC(yr, 11, 31));
    // Week 1 starts on the Sunday on/before Jan 1
    const startDow = jan1.getUTCDay(); // 0=Sun..6=Sat
    const week1Start = new Date(jan1);
    week1Start.setUTCDate(jan1.getUTCDate() - startDow);
    const diffDays = Math.floor((dec31 - week1Start) / 86400000);
    return Math.floor(diffDays / 7) + 1; // can be 52 or 53
  };
  const weeksFromData = (weeksByYear[String(y)] || []);
  const weekTotal = Math.max(sundayWeekCount(y), ...(weeksFromData.length ? weeksFromData : [0]));
  const weeks = Array.from({ length: weekTotal }, (_, i) => i + 1);
  return (
    <Card className="p-3">
      <div className="flex items-center gap-2 flex-wrap">
        <Label className="text-xs font-bold text-primary shrink-0">الفترة:</Label>
        <div className="inline-flex rounded-lg bg-muted p-1 gap-1">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => handlePickType(p.key)}
              className={`px-3 py-1 rounded-md text-[12px] font-bold transition-colors ${periodType === p.key ? 'bg-primary text-white shadow-sm' : 'text-foreground/70 hover:bg-accent'}`}
              data-testid={`period-tab-${p.key}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        {periodType && periodType !== 'custom' && (
          <>
            {/* Year picker */}
            <Select value={String(y)} onValueChange={(v) => onChange(periodType, Number(v), periodValue)}>
              <SelectTrigger className="h-8 text-xs w-28" data-testid="period-year">
                <SelectValue placeholder="السنة" />
              </SelectTrigger>
              <SelectContent>
                {(years || []).map((yr) => <SelectItem key={yr} value={String(yr)}>{yr}</SelectItem>)}
              </SelectContent>
            </Select>
            {/* Value picker based on type */}
            {periodType === 'week' && (
              <Select value={periodValue ? String(periodValue) : ''} onValueChange={(v) => onChange(periodType, y, Number(v))}>
                <SelectTrigger className="h-8 text-xs w-36" data-testid="period-week">
                  <SelectValue placeholder="اختر الأسبوع" />
                </SelectTrigger>
                <SelectContent>
                  {weeks.length === 0 && <SelectItem value="0" disabled>لا توجد أسابيع</SelectItem>}
                  {weeks.map((w) => <SelectItem key={w} value={String(w)}>أسبوع {w}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {periodType === 'month' && (
              <Select value={periodValue ? String(periodValue) : ''} onValueChange={(v) => onChange(periodType, y, Number(v))}>
                <SelectTrigger className="h-8 text-xs w-32" data-testid="period-month">
                  <SelectValue placeholder="اختر الشهر" />
                </SelectTrigger>
                <SelectContent>
                  {MONTH_AR.slice(1).map((m, i) => <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {periodType === 'quarter' && (
              <Select value={periodValue ? String(periodValue) : ''} onValueChange={(v) => onChange(periodType, y, Number(v))}>
                <SelectTrigger className="h-8 text-xs w-32" data-testid="period-quarter">
                  <SelectValue placeholder="اختر الربع" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">الربع الأول</SelectItem>
                  <SelectItem value="2">الربع الثاني</SelectItem>
                  <SelectItem value="3">الربع الثالث</SelectItem>
                  <SelectItem value="4">الربع الرابع</SelectItem>
                </SelectContent>
              </Select>
            )}
            <button
              type="button"
              onClick={() => onChange(null, null, null)}
              className="text-xs font-bold text-destructive hover:underline"
              data-testid="period-clear"
            >
              مسح الفترة
            </button>
          </>
        )}
        {periodType === 'custom' && (
          <>
            <div className="flex items-center gap-1.5">
              <Label className="text-[11px] text-foreground/60 shrink-0">من:</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 text-xs w-36" data-testid="period-custom-from" />
            </div>
            <div className="flex items-center gap-1.5">
              <Label className="text-[11px] text-foreground/60 shrink-0">إلى:</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 text-xs w-36" data-testid="period-custom-to" />
            </div>
            <button
              type="button"
              onClick={() => { onChange(null, null, null); setDateFrom(''); setDateTo(''); }}
              className="text-xs font-bold text-destructive hover:underline"
              data-testid="period-clear"
            >
              مسح النطاق
            </button>
          </>
        )}
      </div>
    </Card>
  );
}

function KpiCircle({ icon, label, value, suffix, sub, small, accent = 'slate', onClick, testid }) {
  const colorMap = {
    emerald: { ring: 'from-emerald-400 to-emerald-600', bg: 'bg-emerald-50', icon: 'text-emerald-700', accent: 'border-emerald-200' },
    blue:    { ring: 'from-blue-400 to-blue-600',       bg: 'bg-blue-50',    icon: 'text-blue-700',    accent: 'border-blue-200' },
    indigo:  { ring: 'from-indigo-400 to-indigo-600',   bg: 'bg-indigo-50',  icon: 'text-indigo-700',  accent: 'border-indigo-200' },
    purple:  { ring: 'from-purple-400 to-purple-600',   bg: 'bg-purple-50',  icon: 'text-purple-700',  accent: 'border-purple-200' },
    rose:    { ring: 'from-rose-400 to-rose-600',       bg: 'bg-rose-50',    icon: 'text-rose-700',    accent: 'border-rose-200' },
    amber:   { ring: 'from-amber-400 to-amber-600',     bg: 'bg-amber-50',   icon: 'text-amber-700',   accent: 'border-amber-200' },
    cyan:    { ring: 'from-cyan-400 to-cyan-600',       bg: 'bg-cyan-50',    icon: 'text-cyan-700',    accent: 'border-cyan-200' },
    slate:   { ring: 'from-slate-400 to-slate-600',     bg: 'bg-slate-50',   icon: 'text-slate-700',   accent: 'border-slate-200' },
  };
  const c = colorMap[accent] || colorMap.slate;
  const clickable = !!onClick;
  // Auto-shrink font for long numeric values so they fit fully inside the circle.
  const valueStr = String(value ?? '');
  const len = valueStr.length;
  let valueSize;
  if (small) valueSize = len >= 10 ? 'text-[9px]' : len >= 8 ? 'text-[10px]' : 'text-[11px]';
  else valueSize = len >= 11 ? 'text-[10px]' : len >= 9 ? 'text-xs' : len >= 7 ? 'text-sm' : 'text-base';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!clickable}
      data-testid={testid}
      className={`group relative flex flex-col items-center justify-center gap-1.5 p-3 rounded-2xl border ${c.accent} ${c.bg} transition-all duration-200 ${clickable ? 'cursor-pointer hover:shadow-lg hover:-translate-y-0.5 hover:scale-[1.03] active:scale-95' : 'cursor-default'}`}
    >
      {/* Circle with gradient ring */}
      <div className={`relative h-20 w-20 rounded-full bg-gradient-to-br ${c.ring} p-[3px] shadow-md ${clickable ? 'group-hover:shadow-xl group-hover:rotate-3 transition-transform duration-300' : ''}`}>
        <div className={`h-full w-full rounded-full bg-white flex flex-col items-center justify-center px-1`}>
          <div className={`absolute -top-1 -right-1 h-7 w-7 rounded-full ${c.bg} ${c.icon} ring-2 ring-white flex items-center justify-center shadow-sm`}>
            {React.cloneElement(icon, { className: 'h-3.5 w-3.5' })}
          </div>
          <div className={`font-black text-foreground leading-none text-center ${valueSize} font-numbers w-full px-0.5 break-all`} dir="ltr">
            {value}
          </div>
          {suffix && <div className="text-[10px] text-foreground/55 mt-0.5">{suffix}</div>}
        </div>
      </div>
      <div className="text-center min-h-[18px] mt-0.5">
        <p className="text-[11px] font-bold text-foreground/75 leading-tight">{label}</p>
        {sub && <p className="text-[10px] text-foreground/55 leading-tight mt-0.5">{sub}</p>}
      </div>
      {clickable && (
        <span className="absolute top-1.5 left-1.5 text-[8px] font-bold text-foreground/35 opacity-0 group-hover:opacity-100 transition-opacity">
          ↗ تفاصيل
        </span>
      )}
    </button>
  );
}

function KpiCard({ icon, label, value, small, accent = 'slate' }) {
  const colorMap = {
    emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
    blue: 'bg-blue-50 text-blue-700 ring-blue-100',
    indigo: 'bg-indigo-50 text-indigo-700 ring-indigo-100',
    purple: 'bg-purple-50 text-purple-700 ring-purple-100',
    rose: 'bg-rose-50 text-rose-700 ring-rose-100',
    amber: 'bg-amber-50 text-amber-700 ring-amber-100',
    cyan: 'bg-cyan-50 text-cyan-700 ring-cyan-100',
    slate: 'bg-slate-50 text-slate-700 ring-slate-100',
  };
  const cls = colorMap[accent] || colorMap.slate;
  return (
    <Card className="p-3 flex items-center gap-3">
      <div className={`h-10 w-10 rounded-lg ring-1 flex items-center justify-center shrink-0 ${cls}`}>
        {React.cloneElement(icon, { className: 'h-5 w-5' })}
      </div>
      <div className="min-w-0">
        <p className="text-xs text-foreground/55">{label}</p>
        <div className={`font-black ${small ? 'text-xs' : 'text-lg'} truncate leading-tight`}>{value}</div>
      </div>
    </Card>
  );
}

function ChartCard({ title, children, id }) {
  return (
    <Card id={id} className="p-4 scroll-mt-24">
      <h3 className="font-bold mb-3 text-sm flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" />{title}</h3>
      {children}
    </Card>
  );
}

function DonutChart({ data, total, large, onSliceClick, activeName }) {
  // Sort + ensure numeric. Preserve `target` and `prev_year_income` (optional).
  const sorted = (data || []).map((d) => ({
    ...d,
    income: Number(d.income || 0),
    target: Number(d.target || 0),
    prev_year_income: Number(d.prev_year_income || 0),
  })).sort((a, b) => b.income - a.income);
  const grand = sorted.reduce((acc, d) => acc + (d.income || 0), 0) || total || 1;
  const grandTarget = sorted.reduce((acc, d) => acc + (d.target || 0), 0);
  const grandPrev = sorted.reduce((acc, d) => acc + (d.prev_year_income || 0), 0);
  const hasTargets = grandTarget > 0;
  const hasPrev = grandPrev > 0;
  if (sorted.length === 0) {
    return <div className="text-center text-foreground/40 text-sm py-8">لا توجد بيانات</div>;
  }
  const height = large ? 380 : 280;
  const outer = large ? 130 : 95;
  const inner = large ? 75 : 55;
  const clickable = !!onSliceClick;
  const handle = (name) => {
    if (!clickable) return;
    onSliceClick(name);
  };
  return (
    <div className={`grid ${large ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1 md:grid-cols-2'} gap-3 items-center`}>
      <div className="relative">
        <ResponsiveContainer width="100%" height={height}>
          <PieChart>
            <Pie
              data={sorted}
              dataKey="income"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={inner}
              outerRadius={outer}
              paddingAngle={1.5}
              stroke="#ffffff"
              strokeWidth={2}
              onClick={(p) => handle(p?.name)}
              style={clickable ? { cursor: 'pointer' } : undefined}
            >
              {sorted.map((d, i) => (
                <Cell
                  key={i}
                  fill={PALETTE[i % PALETTE.length]}
                  stroke={activeName && d.name === activeName ? '#0F172A' : '#ffffff'}
                  strokeWidth={activeName && d.name === activeName ? 3 : 2}
                />
              ))}
            </Pie>
            <Tooltip
              formatter={(v, _n, ctx) => {
                const t = ctx?.payload?.target || 0;
                if (t > 0) {
                  const pct = (v / t) * 100;
                  return [`المحقق ${fmt(v)} / المستهدف ${fmt(t)} ر.س (${pct.toFixed(1)}%)${clickable ? '  •  ↗ انقر للتصفية' : ''}`, ''];
                }
                return [`${fmt(v)} ر.س${clickable ? '  •  ↗ انقر للتصفية' : ''}`, 'الإيراد'];
              }}
              contentStyle={{ borderRadius: 8, fontSize: 12 }}
            />
          </PieChart>
        </ResponsiveContainer>
        {/* Center label — total */}
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-[10px] text-foreground/55 leading-none">المحقق</span>
          <span className="text-sm font-black text-foreground leading-tight">{fmt(grand)}</span>
          {hasTargets && (
            <>
              <span className="text-[9px] text-amber-700 leading-none mt-0.5">من {fmt(grandTarget)}</span>
              <span className="text-[10px] font-bold text-emerald-700 leading-tight">
                {((grand / grandTarget) * 100).toFixed(1)}%
              </span>
            </>
          )}
          {hasPrev && (
            <span className={`text-[9px] font-bold leading-tight mt-0.5 ${(grand - grandPrev) >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}
              title={`محقق العام السابق ${grandPrev.toLocaleString('en-US')} ر.س (${((grandPrev > 0 ? (grand - grandPrev) / grandPrev : 0) * 100).toFixed(1)}%)`}>
              السابق {fmt(grandPrev)}
            </span>
          )}
          <span className="text-[10px] text-foreground/55 leading-none">ر.س</span>
        </div>
      </div>
      {/* Legend list with percentages — clickable */}
      <div className={`${large ? 'max-h-[420px]' : 'max-h-[320px]'} overflow-y-auto pr-1`}>
        <ul className="space-y-2 text-xs">
          {sorted.map((d, i) => {
            const tgtPct = d.target ? (d.income / d.target) * 100 : null;
            const color = PALETTE[i % PALETTE.length];
            const isActive = activeName && d.name === activeName;
            return (
              <li key={i}>
                <button
                  type="button"
                  onClick={() => handle(d.name)}
                  disabled={!clickable}
                  className={`w-full flex flex-col gap-1 rounded-md px-2 py-1.5 border border-transparent transition-colors ${clickable ? 'cursor-pointer hover:bg-accent/60 hover:border-border' : 'cursor-default'} ${isActive ? 'bg-primary/15 ring-1 ring-primary/40' : ''}`}
                  title={clickable ? `انقر لتصفية ${d.name}` : d.name}
                >
                  {/* Row 1: color dot + name + achievement % */}
                  <div className="flex items-center gap-2 w-full">
                    <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: color }} />
                    <span className={`flex-1 truncate text-[12px] font-bold text-right ${isActive ? 'text-primary' : 'text-foreground'}`}>{d.name || '—'}</span>
                    {tgtPct !== null && (
                      <span className={`text-[11px] tabular-nums font-bold ${tgtPct >= 100 ? 'text-emerald-700' : tgtPct >= 70 ? 'text-blue-700' : 'text-rose-600'}`}>
                        {tgtPct.toFixed(0)}%
                      </span>
                    )}
                  </div>
                  {/* Row 2: labeled numbers */}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 pl-4 text-[10px]">
                    {hasTargets && d.target > 0 && (
                      <span className="inline-flex items-center gap-1">
                        <span className="text-amber-700 font-semibold">المستهدف:</span>
                        <span className="font-numbers font-bold text-amber-700">{fmt(d.target)}</span>
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1">
                      <span className="text-foreground/60 font-semibold">المحقق:</span>
                      <span className="font-numbers font-bold text-foreground">{fmt(d.income)}</span>
                    </span>
                    {hasPrev && d.prev_year_income > 0 && (() => {
                      const yoyPct = d.prev_year_income > 0 ? ((d.income - d.prev_year_income) / d.prev_year_income) * 100 : 0;
                      const cls = yoyPct >= 0 ? 'text-emerald-700' : 'text-rose-600';
                      return (
                        <span className="inline-flex items-center gap-1"
                          title={`مقارنة: ${yoyPct >= 0 ? '+' : ''}${yoyPct.toFixed(0)}%`}>
                          <span className="text-blue-700 font-semibold">محقق العام الماضي:</span>
                          <span className={`font-numbers font-bold ${cls}`}>{fmt(d.prev_year_income)}</span>
                        </span>
                      );
                    })()}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

function Th({ children, className = '' }) {
  return <th className={`text-right py-3 px-4 text-xs font-bold whitespace-nowrap ${className}`}>{children}</th>;
}

function Td({ children, className = '' }) {
  return <td className={`py-2 px-4 whitespace-nowrap ${className}`}>{children}</td>;
}

function MultiSelectFilter({ options, selected, onChange, testid, big = false }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = (options || []).map((o) => String(o));
    if (!q) return base;
    return base.filter((o) => o.toLowerCase().includes(q));
  }, [options, query]);
  const toggle = (val) => {
    const set = new Set(selected);
    if (set.has(val)) set.delete(val); else set.add(val);
    onChange(Array.from(set));
  };
  const selectAll = () => onChange(list.slice());
  const clearAll = () => onChange([]);
  const triggerLabel = selected.length === 0
    ? 'الكل'
    : selected.length === 1
      ? selected[0]
      : `${selected.length} مختارة`;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`w-full ${big ? 'h-9 text-sm px-3 rounded-md' : 'h-7 text-[11px] px-2 rounded'} border bg-background flex items-center justify-between gap-1 hover:bg-accent/40 ${selected.length > 0 ? 'border-primary text-primary font-bold' : 'border-border text-foreground/70'}`}
          data-testid={testid}
        >
          <span className="truncate flex-1 text-right" title={selected.join('، ')}>{triggerLabel}</span>
          <ChevronDown className="h-3 w-3 opacity-60 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="end" dir="rtl">
        <div className="p-2 border-b border-border flex items-center gap-1">
          <Search className="h-3 w-3 text-foreground/50 shrink-0" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="بحث..."
            className="flex-1 text-[11px] bg-transparent outline-none"
            data-testid={`${testid}-search`}
          />
        </div>
        <div className="flex items-center justify-between px-2 py-1.5 border-b border-border bg-accent/30">
          <button type="button" onClick={selectAll} className="text-[10px] font-bold text-primary hover:underline" data-testid={`${testid}-select-all`}>
            تحديد {list.length} ({selected.length}/{(options || []).length})
          </button>
          {selected.length > 0 && (
            <button type="button" onClick={clearAll} className="text-[10px] font-bold text-destructive hover:underline" data-testid={`${testid}-clear`}>
              مسح
            </button>
          )}
        </div>
        <div className="max-h-60 overflow-y-auto">
          {list.length === 0 ? (
            <div className="px-3 py-3 text-[11px] text-foreground/50 text-center">لا توجد نتائج</div>
          ) : list.map((o) => {
            const isOn = selected.includes(o);
            return (
              <button
                key={o}
                type="button"
                onClick={() => toggle(o)}
                className={`w-full flex items-center gap-2 px-2 py-1.5 text-[11px] text-right hover:bg-accent/60 transition-colors ${isOn ? 'bg-primary/10 font-bold' : ''}`}
                data-testid={`${testid}-opt-${o}`}
              >
                <Checkbox checked={isOn} className="h-3.5 w-3.5 pointer-events-none" />
                <span className="flex-1 truncate" title={o}>{o}</span>
                {isOn && <Check className="h-3 w-3 text-primary shrink-0" />}
              </button>
            );
          })}
        </div>
        {selected.length > 0 && (
          <div className="px-2 py-1.5 border-t border-border bg-accent/20 text-[10px] text-foreground/60 truncate" title={selected.join('، ')}>
            مختار: {selected.join('، ')}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
